<template>
  <div class="judge-card" @click.prevent="$emit('viewJudge', judge.id)">
    <div class="judge-header">
      <h4 class="judge-name">{{ judge.name }} {{ judge.surname }}</h4>
    </div>

    <div class="judge-body">
      <img :src="judge.img_path || defaultAvatar" alt="Judge" class="judge-image" />

      <div class="judge-info">
        <div class="country-row" v-if="statsCountryCode || isTechOperator">
          <div class="country-badge" v-if="statsCountryCode">
            <span class="country-flag" aria-hidden="true" v-if="countryFlag">{{ countryFlag }}</span>
            <span class="country-code">{{ statsCountryCode }}</span>
          </div>
          <i
              v-if="isTechOperator"
              class="fas fa-video tech-icon text-primary"
              :title="$t('judges.cert.tech_operator') || 'Tech operator'"
              aria-label="Tech operator"
          ></i>
        </div>

        <div class="badge-row" v-if="certificationBadge">
          <span v-if="certificationBadge" class="chip" :class="certificationBadge.class">
            {{ certificationBadge.label }}
          </span>
        </div>

        <div class="stat-grid">
          <div class="stat-card">
            <span class="stat-label">{{ $t('judges.stats.competitions') }}</span>
            <span class="stat-value">{{ stats.competitions_count }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">{{ $t('judges.stats.rounds') }}</span>
            <span class="stat-value">{{ stats.rounds_scored_count }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { isOwnerOrganizer } from '@/helpers/roleUtils';
import { mapState } from 'vuex';

export default {
  name: 'JudgeListCard',
  props: {
    judge: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      defaultAvatar: require('@/assets/user.png'),
    };
  },
  computed: {
    ...mapState('user', ['user']),
    stats() {
      const normalize = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
      };
      const base = this.judge?.stats || {};
      return {
        country_code: (base.country_code || this.judge?.country || '').toUpperCase(),
        competitions_count: normalize(base.competitions_count),
        rounds_scored_count: normalize(base.rounds_scored_count),
      };
    },
    statsCountryCode() {
      return this.stats.country_code;
    },
    countryFlag() {
      return this.statsCountryCode ? this.codeToFlag(this.statsCountryCode) : '';
    },
    certification() {
      return this.judge?.certification || null;
    },
    certificationBadge() {
      if (!this.certification) {
        return { label: this.$t('judges.cert.none') || 'NO TIENE', class: 'chip-none' };
      }
      const level = this.certification?.judge_level?.display_name || this.certification?.judge_level?.name || '';
      const label = level || (this.$t('judges.cert.with') || 'Certificado');
      return { label, class: 'chip-cert' };
    },
    isTechOperator() {
      return Boolean(this.certification?.tech_operator);
    }
  },
  methods: {
    isOwnerOrganizer,
    codeToFlag(code = '') {
      if (!code || code.length < 2) {
        return '';
      }
      const letters = code
        .toUpperCase()
        .replace(/[^A-Z]/g, '')
        .slice(0, 2);
      if (letters.length < 2) {
        return '';
      }
      return letters
        .split('')
        .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
        .join('');
    },
  },
};
</script>

<style scoped>
.judge-card {
  background-color: white;
  border-radius: 10px;
  padding: 0;
  overflow: hidden;
  margin-bottom: 16px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: box-shadow 0.3s ease;
  cursor: pointer;
}

.judge-card:hover {
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.1);
}

.judge-header {
  background-color: #f7f7f7;
  padding: 10px;
  text-align: center;
  border-bottom: 1px solid #ddd;
}

.judge-name {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.judge-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 12px;
}

.judge-image {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  object-fit: cover;
}

.judge-info {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.country-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 999px;
  background-color: #f5f7fa;
  border: 1px solid #e0e6ed;
  width: fit-content;
}

.country-flag {
  font-size: 18px;
  line-height: 1;
  /* Emoji-capable stack so flags render across platforms (incl. Windows) */
  font-family: "Twemoji Mozilla", "Noto Color Emoji", "Segoe UI Emoji", "Apple Color Emoji", "Segoe UI", system-ui, sans-serif;
}

.country-code {
  font-size: 12px;
  letter-spacing: 0.08em;
}

.country-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.tech-icon {
  font-size: 18px;
}

.badge-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid transparent;
}

.chip-cert {
  background: #e6f0ff;
  color: #16409d;
  border-color: #b7cffc;
}

.chip-none {
  background: #fdf2f2;
  color: #991b1b;
  border-color: #fca5a5;
}

.stat-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.stat-card {
  flex: 1;
  min-width: 140px;
  background-color: #fafafa;
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.stat-label {
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  text-align: center;
}

.stat-value {
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  text-align: center;
}

@media (max-width: 576px) {
  .judge-body {
    text-align: center;
  }

  .judge-image {
    width: 80px;
    height: 80px;
  }

  .stat-grid {
    flex-direction: column;
  }
}
</style>
